// Models
import {Beat, beatFromReference, emptyLeadMarks, leadKeyOf, LeadMarks, orderLeads} from "./model"

import {sampleToMsZeroBased, WaveName} from "./globalCsv"

/**
 * The per-lead model's file: `predictions_per_lead.csv` — an INPUT only.
 *
 * One row per wave PER LEAD, each with its own peak:
 *
 *     lead,wave,onset_sample,peak_sample,offset_sample,onset_ms,peak_ms,offset_ms
 *     I,P,50,73,88,100,146,176
 *     I,QRS,106,127,148,212,254,296
 *
 * Some records come with this file instead of `predictions_global.csv`. The
 * technician's job does not change: six GLOBAL marks per beat, exported in the
 * global file's shape. This file only gives them a starting point — the
 * per-lead envelope — and per-lead marks to look at while they correct it.
 *
 * Same conventions as the global file (README of the LUDB 195 sample): samples
 * counted from ZERO, `time_ms = sample * 2` at 500 Hz, onset starts a wave and
 * offset ends it.
 *
 * The leads do NOT have the same number of rows. In LUDB 195 most have 33 and
 * V2 has 35: each lead marks its own waves, and near the end of the record
 * some leads mark the next beat's P although its QRS is cut off. So rows cannot
 * be paired across leads by position — beats are found by WHERE the QRS is.
 */

export type LeadWave = {
    lead: string
    wave: WaveName
    onsetMs: number
    peakMs: number | null
    offsetMs: number
}

/**
 * Which file this is, from its header alone: a `lead` column means per lead.
 *
 * @param text
 */
export function csvKind(text: string): "global" | "perLead" | null {
    const first = text.split(/\r?\n/).filter(line => line.trim() !== "")[0];

    if (!first) {
        return null
    }

    const header = first.split(",").map(cell => cell.trim().toLowerCase());

    if (header.indexOf("wave") === -1) {
        return null
    }

    return header.indexOf("lead") !== -1 ? "perLead" : "global"
}

function readRows(text: string, sampleRate: number): LeadWave[] {
    const lines = text.split(/\r?\n/).filter(line => line.trim() !== "");

    if (!lines.length) {
        return []
    }

    const header = lines[0].split(",").map(cell => cell.trim().toLowerCase());
    const col = (name: string) => header.indexOf(name);

    /* ms columns preferred, sample columns as the fallback — as in globalCsv */
    function at(cells: string[], msName: string, sampleName: string): number | null {
        const ms = col(msName);
        const sample = col(sampleName);
        const raw = ms !== -1 ? cells[ms] : sample !== -1 ? cells[sample] : undefined;

        if (raw === undefined || raw.trim() === "") {
            return null
        }

        const value = Number(raw);

        if (!isFinite(value)) {
            return null
        }

        return ms !== -1 ? value : sampleToMsZeroBased(value, sampleRate)
    }

    const rows: LeadWave[] = [];

    lines.slice(1).forEach(line => {
        const cells = line.split(",").map(cell => cell.trim());
        const lead = cells[col("lead")];
        const wave = (cells[col("wave")] || "").toUpperCase();

        if (!lead || (wave !== "P" && wave !== "QRS" && wave !== "T")) {
            return
        }

        const onsetMs = at(cells, "onset_ms", "onset_sample");
        const offsetMs = at(cells, "offset_ms", "offset_sample");

        if (onsetMs === null || offsetMs === null) {
            return
        }

        rows.push({
            lead: leadKeyOf(lead),
            wave: wave as WaveName,
            onsetMs: onsetMs,
            peakMs: at(cells, "peak_ms", "peak_sample"),
            offsetMs: offsetMs,
        })
    });

    return rows
}

/**
 * How far apart two leads' QRS can be and still be the same beat.
 *
 * Across the twelve leads of one beat the QRS centres sit within a few tens of
 * milliseconds of each other (LUDB 195: under 20 ms); consecutive beats are
 * ~300 ms apart even at 200 bpm. 150 ms sits safely between the two.
 */
const SAME_BEAT_MS = 150;

function centreOf(row: LeadWave): number {
    return row.peakMs !== null ? row.peakMs : (row.onsetMs + row.offsetMs) / 2
}

/**
 * Read the per-lead file into global beats, with the per-lead marks attached
 * as reference.
 *
 * 1. Every lead's QRS is placed on one time line and grouped: a QRS within
 *    `SAME_BEAT_MS` of a group's first member, from a lead the group does not
 *    already have, joins it. Each group is a beat, anchored on the median
 *    centre.
 * 2. Within each lead, the beat's QRS takes that lead's P closest before it and
 *    T closest after it, bounded by the lead's neighbouring QRS — the rule the
 *    global reader uses, applied lead by lead.
 * 3. The six global points are the envelope of those per-lead marks.
 *
 * A wave that belongs to no beat — a P after the last QRS — is left out: it
 * has no global beat to contribute to. A lead with no QRS in a beat has empty
 * marks for it rather than borrowing another lead's.
 *
 * @param text
 * @param sampleRate
 */
export function parsePerLeadCsv(text: string, sampleRate: number): Beat[] {
    return beatsFromLeadWaves(readRows(text, sampleRate))
}

/**
 * The same grouping for per-lead waves that did not come from a file — the
 * rows the annotation endpoint returns.
 *
 * @param waves lead names in any spelling, positions in ms
 */
export function beatsFromLeadWaves(waves: LeadWave[]): Beat[] {
    const rows = waves.map(wave => Object.assign({}, wave, {lead: leadKeyOf(wave.lead)}));
    const leads = orderLeads(rows.map(row => row.lead));

    const complexes = rows
        .filter(row => row.wave === "QRS")
        .sort((a, b) => centreOf(a) - centreOf(b));

    const groups: LeadWave[][] = [];

    complexes.forEach(row => {
        const open = groups.length ? groups[groups.length - 1] : null;

        if (open
            && centreOf(row) - centreOf(open[0]) <= SAME_BEAT_MS
            && !open.some(member => member.lead === row.lead)) {
            open.push(row)
        } else {
            groups.push([row])
        }
    });

    return groups.map((group, index) => {
        const centres = group.map(centreOf).sort((a, b) => a - b);
        const anchor = Math.round(centres[Math.floor(centres.length / 2)]);
        const reference: {[lead: string]: LeadMarks} = {};

        leads.forEach(lead => {
            const own = rows.filter(row => row.lead === lead);
            const ownQrs = own.filter(row => row.wave === "QRS").sort((a, b) => a.onsetMs - b.onsetMs);
            const complex = group.filter(member => member.lead === lead)[0];
            const slot = emptyLeadMarks();

            reference[lead] = slot;

            if (!complex) {
                return
            }

            const at = ownQrs.indexOf(complex);
            const previous = at > 0 ? ownQrs[at - 1] : null;
            const next = at < ownQrs.length - 1 ? ownQrs[at + 1] : null;

            const p = own
                .filter(row => row.wave === "P" &&
                    row.offsetMs <= complex.onsetMs &&
                    (previous === null || row.onsetMs > previous.offsetMs))
                .sort((a, b) => a.onsetMs - b.onsetMs)
                .pop();

            const t = own
                .filter(row => row.wave === "T" &&
                    row.onsetMs >= complex.offsetMs &&
                    (next === null || row.offsetMs < next.onsetMs))
                .sort((a, b) => a.onsetMs - b.onsetMs)
                .shift();

            [{row: p, prefix: "p"}, {row: complex, prefix: "qrs"}, {row: t, prefix: "t"}].forEach(wave => {
                if (!wave.row) {
                    return
                }

                slot[(wave.prefix + "Start") as keyof LeadMarks] = wave.row.onsetMs;
                slot[(wave.prefix + "Peak") as keyof LeadMarks] = wave.row.peakMs;
                slot[(wave.prefix + "End") as keyof LeadMarks] = wave.row.offsetMs;
            })
        });

        return beatFromReference(index + 1, anchor, reference)
    })
}
